import React from 'react';
import { IconPencil, IconClock, IconChecklist, IconCheckbox } from '@tabler/icons-react';

const ReadyToStartTest = ({ onStartTest }: { onStartTest(): void }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 p-6">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Highlight bar animation */}
        <div className="h-1 bg-gray-100 w-full">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 w-full"></div>
        </div>
        
        <div className="p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Ready to Start Your Test?</h2>
          
          {/* Test and clock illustration */}
          <div className="relative h-32 mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Simplified test icon orbit with just test and clock */}
              <div className="test-icon-container">
                <div className="test-paper animate-float">
                  <IconChecklist className="text-indigo-500 h-12 w-12" />
                </div>
                <div className="test-clock animate-float-delayed">
                  <IconClock className="text-purple-500 h-12 w-12" />
                </div>
                
                {/* Center glow */}
                <div className="absolute h-16 w-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full animate-pulse flex items-center justify-center">
                  <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center">
                    <div className="h-8 w-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Ready message with inspiration text */}
          <div className="text-center mb-8">
            <p className="text-gray-600 text-lg font-medium mb-2">Your test is prepared and waiting</p>
            <p className="text-gray-500 text-sm">Take a deep breath and get ready to focus</p>
          </div>
          
          {/* Paper sheets illustration */}
          <div className="relative h-20 mx-auto w-40 mb-8">
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full transform rotate-3 top-3 left-2 animate-stack-1"></div>
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full transform -rotate-2 top-2 left-0 animate-stack-2"></div>
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full transform rotate-1 top-1 -left-2 animate-stack-3"></div>
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full top-0 left-0 z-10">
              <div className="h-2 w-12 bg-gray-200 rounded mb-2"></div>
              <div className="h-2 w-24 bg-gray-200 rounded mb-2"></div>
              <div className="h-2 w-16 bg-gray-200 rounded"></div>
            </div>
          </div>
          
          {/* Call to action button */}
          <div className="mt-6 text-center">
            <button 
              onClick={onStartTest}
              className="px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium rounded-full shadow-lg hover:shadow-xl transform transition hover:-translate-y-1 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Start Test Now
              <IconPencil className="inline-block ml-2 h-5 w-5" />
            </button>
          </div>
          
          {/* Additional note */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-xs">Make sure you're in a quiet environment with no distractions</p>
          </div>
        </div>
      </div>
      
      {/* CSS animations */}
      <style jsx="true">{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes stack-hover-1 {
          0%, 100% { transform: rotate(3deg) translateY(0); }
          50% { transform: rotate(3deg) translateY(-2px); }
        }
        
        @keyframes stack-hover-2 {
          0%, 100% { transform: rotate(-2deg) translateY(0); }
          50% { transform: rotate(-2deg) translateY(-3px); }
        }
        
        @keyframes stack-hover-3 {
          0%, 100% { transform: rotate(1deg) translateY(0); }
          50% { transform: rotate(1deg) translateY(-1px); }
        }
        
        .test-icon-container {
          position: relative;
          width: 130px;
          height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .test-paper {
          position: absolute;
          left: 0;
          animation: float 3s infinite ease-in-out;
        }
        
        .test-clock {
          position: absolute;
          right: 0;
          animation: float-delayed 3.5s infinite ease-in-out;
        }
        
        .animate-float {
          animation: float 3s infinite ease-in-out;
        }
        
        .animate-float-delayed {
          animation: float-delayed 3.5s infinite ease-in-out;
          animation-delay: 0.2s;
        }
        
        .animate-stack-1 {
          animation: stack-hover-1 3s infinite ease-in-out;
        }
        
        .animate-stack-2 {
          animation: stack-hover-2 4s infinite ease-in-out;
        }
        
        .animate-stack-3 {
          animation: stack-hover-3 3.5s infinite ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default ReadyToStartTest;
